import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@material-ui/core';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import trI18n from '@/common/utils/trI18n';
import { useTranslationSubscription } from '@/hooks/useTranslation';
import './UserProfile.css';
import LoadingPanel from '../common/LoadingPanel';
import ConfirmDialog from '@/common/dialog/ConfirmDialog';
import Utils from '@/common/utils/utils';
import localStorageUtil from '@/common/utils/localStorageUtil';
import { PRECONDITION_ERROR_CODES, ZIP_ERROR_CODES } from '@/common/constant/McpBootstrapConstant';
import { generateMCPBootstrap, downloadMCPBootstrap, getMCPBootstrapStatus } from '@/service/mcpBootstrapService';
import DownloadIcon from '../common/icons/DownloadIcon';
import { useRecoilValue } from 'recoil';
import { userAtom } from '@/state/atom/userAtom';
import { formatUserDateTime } from '@/common/utils/timezoneUtils';
import Constant from '@/common/constant/Constant';

const resolveErrorMessage = errorCode => {
    if (PRECONDITION_ERROR_CODES.includes(errorCode)) {
        return trI18n('COMMSG030');
    }
    if (ZIP_ERROR_CODES.includes(errorCode)) {
        return trI18n('MCP_LOCAL_DOWNLOAD_ERROR');
    }
    // Default (covers connection errors and unknown codes): connection error message.
    return trI18n('MCP_LOCAL_CONNECTION_ERROR');
};


export default function LocalMCPIntegrationTab({ isDisplayLocalMcpTab }) {
    // Re-render when the translation dictionary loads/updates
    useTranslationSubscription();
    const userDetail = useRecoilValue(userAtom);

    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const [mcpStatus, setMcpStatus] = useState(null);

    const corporationId = localStorageUtil.getAffiliationOrganization();
    const projectId = localStorageUtil.getSelectedProjectId();
    const userId = localStorageUtil.getUserDetailId();

    const bootstrapVersion = mcpStatus?.bootstrapVersion || null;
    const downloadedAt = mcpStatus?.downloadedAt || null;
    const latestVersion = mcpStatus?.latestVersion || null;
    const latestReleaseDate = mcpStatus?.latestReleaseDate || null;

    const isDownloaded = !Utils.isEmptyString(bootstrapVersion);

    const showUpdateNotice = !!(
        (mcpStatus?.hasUpdate || mcpStatus?.hasMcpCatalogUpdate) &&
        isDownloaded &&
        latestVersion
    );

    const loadMcpStatus = useCallback(async () => {
        if (!isDisplayLocalMcpTab) return;
        const result = await getMCPBootstrapStatus(corporationId, projectId);
        
        if (!result?.eligible) {
            setErrorMessage(result?.errorCode
                ? resolveErrorMessage(result?.errorCode)
                : trI18n('COMMSG030')
            );
            return;
        }

        setMcpStatus(result);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [corporationId, projectId, userId, isDisplayLocalMcpTab]);

    // load init
    useEffect(() => {
        if (!isDisplayLocalMcpTab) return;

        setIsLoading(true);
        loadMcpStatus().then(() => setIsLoading(false)).catch(error => {
            console.log('loadMcpStatus error >>>', error);
            setIsLoading(false)
        })
    }, [loadMcpStatus, isDisplayLocalMcpTab])

    // FE-04: calls the generate API then downloads via the presigned URL. Guards against
    // double-clicks with isLoading.
    const handleDownload = async () => {
        if (isLoading) return;
        setIsLoading(true);
        try {
            const result = await generateMCPBootstrap(corporationId, projectId);
            if (!result?.success) {
                // FE-06: always turn off loading and show the error popup.
                setErrorMessage(resolveErrorMessage(result?.errorCode));
                return;
            }

            const corpDomain = localStorageUtil.getLastLoginCorpDomain();
            downloadMCPBootstrap(
                result.downloadUrl,
                `bootstrap_personal_${corpDomain}.zip`
            );
            // FE-05: download succeeded → refresh status (version + install date) and hide
            // the upgrade notice.
            await loadMcpStatus();
        } catch (err) {
            setErrorMessage(resolveErrorMessage(null));
        } finally {
            setIsLoading(false);
        }
    };

    if (!isDisplayLocalMcpTab) return <></>;

    return (
        <div className="settingFrame scrollTab customScroll">
            <LoadingPanel isLoading={isLoading} />
            <div className="localMcpIntegration">
                <h3 className="localMcp-title">{trI18n('MCP_LOCAL_INTEGRATION_TITLE')}</h3>

                {isDownloaded && (
                    <>
                        <div className="localMcp-infoRow">
                            <span className="localMcp-infoLabel">
                                {trI18n('MCP_LOCAL_CURRENT_VERSION')}
                            </span>
                            <span className="localMcp-infoValue">{`v${bootstrapVersion}`}</span>
                        </div>

                        <div className="localMcp-infoRow">
                            <span className="localMcp-infoLabel">
                                {trI18n('MCP_LOCAL_INSTALL_DATE')}
                            </span>
                            <span className="localMcp-infoValue">
                                {formatUserDateTime(
                                    downloadedAt,
                                    Constant.MOMENT_DATE_FORMAT.YYYYMMDD_HHMM,
                                    userDetail?.userSetting
                                ) || '-'}
                            </span>
                        </div>
                    </>
                )}

                {showUpdateNotice && (
                    <div className="localMcp-updateBlock">
                        <div className="localMcp-infoRow">
                            <span className="localMcp-infoLabel">
                                {trI18n('MCP_LOCAL_UPDATE_DATE')}
                            </span>
                            <span className="localMcp-infoValue">
                                {formatUserDateTime(
                                    latestReleaseDate,
                                    Constant.MOMENT_DATE_FORMAT.YYYYMMDD_HHMM,
                                    userDetail?.userSetting
                                ) || '-'}
                            </span>
                        </div>

                        <div className="localMcp-infoRow">
                            <span className="localMcp-infoLabel">
                                {trI18n('MCP_LOCAL_LATEST_VERSION')}
                            </span>
                            <span className="localMcp-infoValue">{`v${latestVersion}`}</span>
                        </div>

                        <div className="localMcp-updateNotice">
                            <WarningRoundedIcon className="localMcp-updateNoticeIcon" />
                            <span>{trI18n('MCP_LOCAL_UPDATE_AVAILABLE')}</span>
                        </div>
                    </div>
                )}

                <div className="localMcp-actions">
                    <Button
                        variant="contained"
                        color="primary"
                        className="localMcp-downloadBtn"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownload}
                        disabled={isLoading}
                    >
                        {trI18n('MCP_LOCAL_DOWNLOAD_BTN')}
                    </Button>
                </div>
            </div>

            {/* FE-06: error popup with a single OK button */}
            <ConfirmDialog
                isOpen={!Utils.isEmptyString(errorMessage)}
                msgConfirm={errorMessage}
                okCallBack={() => window.location.reload()}
                textSubmit={trI18n('CM-BUTTON-OK')}
            />
        </div>
    );
}
